import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ShieldAlert, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export type RegulatoryTier = "red" | "yellow" | "green";

interface ComplianceBadgeProps {
  tier: RegulatoryTier;
  showLabel?: boolean;
  showIcon?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

/**
 * 合规徽章组件
 * 根据 regulatory_tier 显示红/黄/绿徽章
 *
 * - red: 处方药/严格限制 (FDA GFI #263)
 * - yellow: OTC 化学药剂/强副作用
 * - green: 安全/物理/天然
 */
export function ComplianceBadge({
  tier,
  showLabel = true,
  showIcon = true,
  size = "md",
  className,
}: ComplianceBadgeProps) {
  const { t } = useTranslation("common");

  const config = React.useMemo(() => {
    switch (tier) {
      case "red":
        return {
          variant: "destructive" as const,
          label: t("compliance.tier.red", "Restricted"),
          icon: ShieldAlert,
          ariaLabel: t(
            "compliance.tier.redAria",
            "Restricted item - professional guidance recommended"
          ),
        };
      case "yellow":
        return {
          variant: "outline" as const,
          label: t("compliance.tier.yellow", "Caution"),
          icon: AlertTriangle,
          ariaLabel: t("compliance.tier.yellowAria", "Caution - use with care"),
          customClass:
            "border-amber-500 text-amber-600 dark:border-amber-400 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30",
        };
      case "green":
      default:
        return {
          variant: "secondary" as const,
          label: t("compliance.tier.green", "Safe"),
          icon: ShieldCheck,
          ariaLabel: t("compliance.tier.greenAria", "Safe to use"),
          customClass:
            "border-emerald-500 text-emerald-600 dark:border-emerald-400 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30",
        };
    }
  }, [tier, t]);

  const sizeClasses = {
    sm: "text-[10px] px-1.5 py-0 [&>svg]:size-2.5",
    md: "text-xs px-2 py-0.5 [&>svg]:size-3",
    lg: "text-sm px-2.5 py-1 [&>svg]:size-4",
  };

  const Icon = config.icon;

  // 绿灯物品默认不显示徽章（除非明确要求）
  if (tier === "green" && !showLabel) {
    return null;
  }

  return (
    <Badge
      variant={config.variant}
      className={cn(sizeClasses[size], config.customClass, className)}
      aria-label={config.ariaLabel}
    >
      {showIcon && <Icon className="shrink-0" />}
      {showLabel && <span>{config.label}</span>}
    </Badge>
  );
}

/**
 * 合规指示点组件
 * 用于搜索结果列表中的简洁显示
 */
export function ComplianceDot({
  tier,
  className,
}: {
  tier: RegulatoryTier;
  className?: string;
}) {
  const { t } = useTranslation("common");

  const config = React.useMemo(() => {
    switch (tier) {
      case "red":
        return {
          color: "bg-red-500",
          ariaLabel: t("compliance.tier.redAria", "Restricted item"),
        };
      case "yellow":
        return {
          color: "bg-amber-500",
          ariaLabel: t("compliance.tier.yellowAria", "Caution item"),
        };
      case "green":
      default:
        return {
          color: "bg-emerald-500",
          ariaLabel: t("compliance.tier.greenAria", "Safe item"),
        };
    }
  }, [tier, t]);

  return (
    <span
      className={cn(
        "inline-block size-2 rounded-full shrink-0",
        config.color,
        className
      )}
      aria-label={config.ariaLabel}
      role="img"
    />
  );
}

export default ComplianceBadge;
